import groupBy from "lodash/groupBy";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import keyBy from "lodash/keyBy";
import keys from "lodash/keys";
import last from "lodash/last";
import mapValues from "lodash/mapValues";
import pickBy from "lodash/pickBy";
import sortBy from "lodash/sortBy";
import toPairs from "lodash/toPairs";
import values from "lodash/values";
import { checkIsExpandable } from "PFApp/booking/parts/overview/side_panel_row/side_panel_row.utils";
import { Row, RowType } from "PFApp/booking/types";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { isProjects } from "PFApp/booking/utils/calendar_root.utils";

/**
 * `unset` - state is reset;
 * `off` - state was manually set to off;
 * `all` - all applicable children are marked. It triggers new children to be marked;
 * `partial` - the state was `all` before, but now not every child is marked.
 *             It triggers new children to be marked;
 */
type AllBookingsModeItemState = "all" | "partial" | "off" | "unset";
type Item = { key: string; parents: string[] };
type CalculateAllBookingsModeState<T extends Item> = {
  allBookingsModeState: Record<string, AllBookingsModeItemState | undefined>;
  items: T[];
  isApplicable: (item: T) => boolean;
};

const resetItemsState = <T extends Item>({
  allBookingsModeState,
  items,
  isApplicable
}: CalculateAllBookingsModeState<T>): Record<string, AllBookingsModeItemState | undefined> => ({
  ...mapValues(
    keyBy(items.filter(isApplicable), ({ key }) => key),
    () => "unset"
  ),
  ...allBookingsModeState
});

/**
 * Calculates the "all bookings mode" state basing on the previous state and current items.
 *
 * This function may not return correct results if the input data is ambiguous.
 * For example - parent: disabled, child: enabled.
 * In this case it can be resolved to: both parent and child: enabled OR both disabled.
 * The input data needs to be predictable - in the moment of marking parent as off, children should be reset.
 * Only then such the state should be passed to the function.
 *
 * - It marks new children by default
 * - It preserves the state of the children even if the children are not visible
 */
export const calculateAllBookingsModeState = <T extends Item>({
  allBookingsModeState,
  items,
  isApplicable
}: CalculateAllBookingsModeState<T>): Record<string, AllBookingsModeItemState | undefined> => {
  const result: Record<string, AllBookingsModeItemState | undefined> = resetItemsState({
    allBookingsModeState,
    items,
    isApplicable
  });
  const children = items.filter((item) => !isEmpty(item.parents));

  // if the state of the child is undefined or unset, then mark the child
  for (const child of children) {
    const parent = last(child.parents);
    const parentState = parent && result[parent];
    if (
      parentState &&
      ["all", "partial"].includes(parentState) &&
      isNil(allBookingsModeState[child.key]) &&
      isApplicable(child)
    ) {
      result[child.key] = "all";
    }
  }

  // if an item is not applicable, make sure its state is reset
  for (const item of items) {
    if (!isApplicable(item)) {
      keys(result).forEach((key) => {
        if (key.includes(item.key)) {
          result[key] = "unset";
        }
      });
    }
  }

  // Go bottom up and update parents states basing on their children states. Do it level by level.
  // It uses the fact all available items are initially marked as `unset`
  const itemsByParentsCount = groupBy(items, (item) => item.parents.length) as Record<number, Item[]>;
  const itemsLevels = sortBy(toPairs(itemsByParentsCount), ([count]) => -Number(count)).map(
    ([, group]) => group
  );

  for (const itemsInLevel of itemsLevels) {
    for (const item of itemsInLevel) {
      const childrenState = pickBy(result, (value, key) => key !== item.key && key.includes(item.key));
      const knownChildrenCount = keys(childrenState).length;
      if (knownChildrenCount === 0) {
        continue;
      }
      const childrenStates = values(childrenState);
      const enabledChildrenCount = childrenStates.filter(
        (state) => state === "partial" || state === "all"
      ).length;
      if (enabledChildrenCount === 0) {
        const newState = result[item.key] === "off" ? "off" : "unset";
        result[item.key] = newState;
        if (newState === "unset") {
          // if we calculated an item's state as "unset", then we need to reset all its children states
          for (const key of keys(result)) {
            if (key !== item.key && key.includes(item.key)) {
              result[key] = "unset";
            }
          }
        }
      } else if (enabledChildrenCount === childrenStates.length) {
        result[item.key] = "all";
      } else if (enabledChildrenCount < childrenStates.length && result[item.key] === "all") {
        // parent is marked as partial only in case it was `all` before
        result[item.key] = "partial";
      }
    }
  }
  return result;
};

export const checkIsAllBookingsModeApplicable = (
  row: Row,
  calendarRoot: CalendarRoot.ProjectsRoles | CalendarRoot.ProjectsEngagements
) => checkIsExpandable(calendarRoot, row, null) || (isProjects(calendarRoot) && row.type === RowType.Profile);
